import moment from 'moment'

import { overviewFilterOptionsEnum } from 'modules/project_management/lib/constants'

import { iProjectTimeRange } from './useGetProjectTimeRanges'

export interface graphDates {
  startDate: moment.Moment
  endDate: moment.Moment
  slotType: string
}

const currentWeek: graphDates = {
  startDate: moment().startOf('week'),
  endDate: moment().endOf('week'),
  slotType: 'days',
}

const lastWeek: graphDates = {
  startDate: moment().subtract(1, 'weeks').startOf('week'),
  endDate: moment().subtract(1, 'weeks').endOf('week'),
  slotType: 'days',
}

const currentMonth: graphDates = {
  startDate: moment().startOf('month'),
  endDate: moment().endOf('month'),
  slotType: 'weeks',
}

const lastMonth: graphDates = {
  startDate: moment().subtract(1, 'months').startOf('month'),
  endDate: moment().subtract(1, 'months').endOf('month'),
  slotType: 'weeks',
}

const getWholeProjectStartDate = (
  estimatedStartDate: string | null,
  realStartDate: string | null
) => {
  const mEstimatedStartDate = estimatedStartDate ? moment(estimatedStartDate) : null
  const mRealStartDate = realStartDate ? moment(realStartDate) : null

  if (mEstimatedStartDate && !mRealStartDate) return mEstimatedStartDate
  if (!mEstimatedStartDate && mRealStartDate) return mRealStartDate
  if (mEstimatedStartDate && mRealStartDate) {
    return mEstimatedStartDate.isBefore(mRealStartDate) ? mEstimatedStartDate : mRealStartDate
  }
  return null
}

const getWholeProjectEndDate = (estimatedEndDate: string | null, realEndDate: string | null) => {
  const mEstimatedEndDate = estimatedEndDate ? moment(estimatedEndDate) : null
  const mRealEndDate = realEndDate ? moment(realEndDate) : null

  if (mEstimatedEndDate && !mRealEndDate) return mEstimatedEndDate
  if (!mEstimatedEndDate && mRealEndDate) return mRealEndDate
  if (mEstimatedEndDate && mRealEndDate) {
    return mEstimatedEndDate.isAfter(mRealEndDate) ? mEstimatedEndDate : mRealEndDate
  }
  return null
}

const calculateWholeProjectViewDates = (
  startDate: moment.Moment | null,
  endDate: moment.Moment | null
): { startDate: moment.Moment; endDate: moment.Moment } => {
  const newStartDate = startDate || moment().startOf('week')
  const newEndDate = endDate || moment().endOf('week')
  if (startDate) {
    if (endDate) {
      if (startDate == endDate) {
        return { startDate: startDate.startOf('week'), endDate: endDate.endOf('week') }
      }
      return { startDate: startDate, endDate: endDate }
    } else {
      return { startDate: startDate, endDate: startDate.add(1, 'weeks') }
    }
  } else {
    if (endDate) {
      return { startDate: endDate.subtract(1, 'weeks'), endDate: endDate }
    } else {
      return { startDate: newStartDate, endDate: newEndDate }
    }
  }
}

const getWholeProjectDates = (projectTimeRange: iProjectTimeRange) => {
  const { estimatedStartDate, estimatedDueDate, realStartDate, realDueDate } = projectTimeRange

  const startDate = getWholeProjectStartDate(estimatedStartDate ?? null, realStartDate ?? null)
  const endDate = getWholeProjectEndDate(estimatedDueDate ?? null, realDueDate ?? null)

  return calculateWholeProjectViewDates(startDate, endDate)
}

const getWholeProjectSlotType = (startDate: moment.Moment, endDate: moment.Moment) => {
  const days = endDate.diff(startDate, 'days')
  if (days <= 7) return 'days'
  const months = endDate.diff(startDate, 'months')
  if (months <= 2) return 'weeks'
  if (months <= 12) return 'months'
  return 'years'
}

const wholeProject = (projectTimeRanges?: iProjectTimeRange[]): graphDates => {
  if (!projectTimeRanges) return currentWeek

  const projectTimeRange = projectTimeRanges[0]
  if (!projectTimeRange) return currentWeek
  const { startDate, endDate } = getWholeProjectDates(projectTimeRange)

  return {
    startDate: startDate,
    endDate: endDate,
    slotType: getWholeProjectSlotType(startDate, endDate),
  }
}

export const useGetSelectedGraphDates = (
  selectedFilter: string | undefined,
  projectTimeRanges?: iProjectTimeRange[]
): graphDates => {
  switch (selectedFilter) {
    case overviewFilterOptionsEnum.currentWeek:
      return currentWeek
    case overviewFilterOptionsEnum.lastWeek:
      return lastWeek
    case overviewFilterOptionsEnum.currentMonth:
      return currentMonth
    case overviewFilterOptionsEnum.lastMonth:
      return lastMonth
    default:
      return wholeProject(projectTimeRanges)
  }
}
